/* eslint-env node */
import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Footer from "./Footer";
import TopBar from "./Topbar";
import useConfig from "../hooks/useConfig";
import { loadPublicConfigs } from "../redux/slices/configSlice";
import { getRecaptchaToken } from "../utils/recaptcha";

import { notifyUser } from "../utils/notifyUser";

// Get backend URL from environment or use default
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function InvestorApplicationForm() {
    // Dynamically load reCAPTCHA script only for this component
    const { getConfig } = useConfig();
    const dispatch = useDispatch();
    const googleRecaptchaEnabled = getConfig("RECAPTCHA_ENABLED") === "true";

    // Load public configs when component mounts
    useEffect(() => {
        dispatch(loadPublicConfigs()).catch(() => {
            // Error loading configs
        });
    }, [dispatch]);

    useEffect(() => {
        if(googleRecaptchaEnabled) {
            const scriptId = "recaptcha-enterprise-script";
            if(!document.getElementById(scriptId)) {
                const script = document.createElement("script");
                const siteKey = getConfig("RECAPTCHA_SITE_KEY");
                script.id = scriptId;
                script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
                script.async = true;
                script.defer = true;
                document.body.appendChild(script);
            }
            return () => {
                const script = document.getElementById(scriptId);
                if(script) script.remove();
            };
        }
    }, [getConfig]);
    // States for form fields

    const [, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [email, setEmail] = useState("");
    const [showUsernameSection] = useState(false);
    const [, setIsSubmitting] = useState(false);
    const [authMethod, setAuthMethod] = useState("credentials");
    const [, setIsSubmitted] = useState(false);
    const [pendingOAuthProfile, setPendingOAuthProfile] = useState(null);
    const [isCompletingOAuth, setIsCompletingOAuth] = useState(false);

    const navigate = useNavigate();

    const googleEnabled =
        getConfig("GOOGLE_AUTH_ENABLED")?.toLowerCase() === "true";

    const microsoftEnabled =
        getConfig("MICROSOFT_AUTH_ENABLED")?.toLowerCase() === "true";

    const titleRef = useRef(null);

    // Defensive: get config values and check for null/empty before using
    const emailRegexStr = getConfig("EMAIL_REGEX");
    const passwordMinLengthStr = getConfig("REACT_APP_PASSWORD_MIN_LENGTH");
    // Fallbacks and error handling
    const emailRegex = emailRegexStr
        ? new RegExp(emailRegexStr)
        : /^.+@.+\..+$/;
    const passwordMinLength = passwordMinLengthStr
        ? parseInt(passwordMinLengthStr, 10)
        : 8;

    // Load password regex from config with safe fallback
    const passwordRegexCfg = getConfig("REACT_APP_PASSWORD_REGEX");
    let passwordAllowedRegex;
    try {
        passwordAllowedRegex = passwordRegexCfg
            ? new RegExp(passwordRegexCfg)
            : /^[a-zA-Z0-9#$&_\-+%|\[\]@;!()]+$/;
    } catch (e) {
        passwordAllowedRegex = /^[a-zA-Z0-9#$&_\-+%|\[\]@;!()]+$/;
    }

    const isStrongPassword = (password) => {
        if (!password) return false;
        if (password.length < passwordMinLength) return false;
        return passwordAllowedRegex.test(password);
    };

    const [showPassword, setShowPassword] = useState(false);

    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Load form data from localStorage
    const loadFromLocalStorage = () => {
        const savedData = localStorage.getItem("investorFormData");

        if(savedData) {
            const formData = JSON.parse(savedData);

            setUsername(formData.username || "");
            setEmail(formData.email || "");
        }
    };

    // Load data from localStorage on component mount
    useEffect(() => {
        loadFromLocalStorage();
    }, []);

    useEffect(() => {
        const loadPendingOAuthProfile = async() => {
            try {
                const { data } = await axios.get(
                    `${BACKEND_URL}/api/auth/oauth/pending-registration`,
                    { withCredentials: true },
                );

                if(data.pending) {
                    setPendingOAuthProfile(data);
                    setEmail(data.email || "");
                    setUsername(data.email?.split("@")[0] || "");
                    setAuthMethod("oauth");
                }
            } catch(error) {
                console.error("Failed to load pending OAuth registration:", error);
            }
        };

        loadPendingOAuthProfile();
    }, []);

    useEffect(() => {
        if(titleRef.current) {
            titleRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, []);

    const handleOAuthSignup = (provider) => async () => {
        try {
            // Save role before OAuth
            localStorage.setItem("oauthRole", "investor");

            await axios.post(
                `${BACKEND_URL}/api/auth/set-role`,
                {
                    role: "investor",
                },
                { withCredentials: true },
            );

            // Then redirect to Google/Microsoft login - include debug query param
            window.location.href = `${BACKEND_URL}/api/auth/${provider}?source=investorApplication&intent=signup`;
        } catch{
            notifyUser(null, "error", "Failed to start OAuth flow. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const completePendingOAuthRegistration = async() => {
        if(!pendingOAuthProfile) return;

        setIsCompletingOAuth(true);
        try {
            const { data } = await axios.post(
                `${BACKEND_URL}/api/auth/oauth/complete-registration`,
                { role: "investor" },
                { withCredentials: true },
            );

            window.location.href = `/oauth-complete?token=${encodeURIComponent(data.token)}&email=${encodeURIComponent(data.email)}`;
        } catch(error) {
            if(error.response?.status === 410) {
                setPendingOAuthProfile(null);
            }

            notifyUser(null, "error", error.response?.data?.error ||
                "Unable to complete registration. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setIsCompletingOAuth(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setIsSubmitting(true);

        // BLOCK if phone not verified

        if(!email || !password || !confirmPassword) {
            notifyUser(null, "error", "Please fill out all fields.", "toast", null, null, null, null, null, false, false, "top-center");

            return;
        }

        if(password !== confirmPassword) {
            notifyUser(null, "error", "Passwords do not match.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        if(password.length < passwordMinLength) {
            notifyUser(null, "error", `Password must be at least ${passwordMinLength} characters long`, "toast", null, null, null, null, null, false, false, "top-center");

            return;
        }

        if(!isStrongPassword(password)) {
            notifyUser(null, "error", `Password must be at least ${passwordMinLength} characters and may only contain letters, numbers, and these special characters: # $ & _ - + % | [ ] @ ; ! ( )`, "toast", null, null, null, null, null, false, false, "top-center");

            return;
        }

        if(!emailRegex.test(email)) {
            notifyUser(null, "error", "Enter a valid email address", "toast", null, null, null, null, null, false, false, "top-center");

            return;
        }

        if(password === email) {
            notifyUser(null, "error", "Password should not be the same as username", "toast", null, null, null, null, null, false, false, "top-center");

            return;
        }

        setIsSubmitting(true);

        try {
            let siteKey = null;
            let recaptcha_token = null;
            let recaptcha_action = null;

            if(googleRecaptchaEnabled) {
                siteKey = getConfig("RECAPTCHA_SITE_KEY");
                recaptcha_action = "registration";
                recaptcha_token = await getRecaptchaToken(
                    recaptcha_action,
                    siteKey,
                );

                if(!recaptcha_token) {
                    notifyUser(null, "error", "Couldn't get verification token. Check your network/ad-blocker and try again.", "toast", null, null, null, null, null, false, false, "top-center");
                    setIsSubmitting(false);
                    return;
                }
            } else {
                recaptcha_action = "registration";
            }

            // Ensure recaptcha_action is always defined
            recaptcha_action = recaptcha_action || "registration";

            const response = await axios.post(
                `${BACKEND_URL}/api/investor`,
                {
                    email,
                    password,
                    confirmPassword,
                    recaptcha_token,
                    recaptcha_action,
                },
                { withCredentials: true },
            );

            if(response.status === 200) {
                notifyUser(null, "success", <div>
                        <h4 className="text-navy font-semibold">Check Your Email to Continue</h4>
                        <div className="text-sm text-gray-700 mt-1">
                            We&apos;ve sent a verification link to the email address you provided. Please check your inbox (and spam folder, just in case) and click the link to continue your application.
                        </div>
                    </div>, "toast", null, null, 8000, null, null, false, false, "top-center");
                localStorage.removeItem("entrepreneurFormData");
                localStorage.removeItem("investorFormData");
                setIsSubmitted(true);

                // Only navigate to login on successful submission; pass a simple text toast
                setTimeout(() =>
                    navigate("/login", {
                        state: {
                            verificationToastText:
                                "We've sent a verification link to the email address you provided. Please check your inbox (and spam folder, just in case) and click the link to continue your application.",
                        },
                    }),
                8000,
                );
            } else {
                notifyUser(null, "error", "Unexpected response. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(error) {
            if(error.response) {
                const errorMessage =
                    error.response.data?.message ||
                    error.response.data?.error ||
                    "Submission failed.";
                notifyUser(null, "error", errorMessage, "toast", null, null, null, null, null, false, false, "top-center");
            } else if(error.request) {
                notifyUser(null, "error", "Network error: Unable to connect to server. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                notifyUser(null, "error", "An unexpected error occurred. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <TopBar />
            {/* Progress Bar */}
            <div className="bg-gray-100 p-4">
                <div className="max-w-6xl mx-auto">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">
                            Step 2 of 5
                        </span>
                        <span className="text-sm text-gray-600">40%</span>
                    </div>
                    <div className="w-full bg-gray-300 rounded-full h-2">
                        <div
                            className="bg-navy h-2 rounded-full transition-all duration-300"
                            style={{ width: "40%" }}
                        ></div>
                    </div>
                </div>
            </div>
            <div className="font-poppins">
                <div className="flex flex-col min-h-screen bg-white font-poppins">
                    <main className="flex justify-center flex-grow">
                        <div
                            ref={titleRef}
                            className="w-full max-w-6xl px-4 pt-2 pb-4 bg-white shadow-xl rounded-xl sm:px-6 md:px-8"
                        >
                            {/* Title & Description */}
                            {!showUsernameSection && (
                                <div className="flex flex-col items-center mt-4 mb-2">
                                    <h1 className="text-center text-3xl font-semibold text-navy font-poppins">
                                        INVESTOR APPLICATION
                                    </h1>
                                    <p className="text-center text-lg text-navy  font-medium font-poppins">
                                        GETTING STARTED
                                    </p>
                                    <p className="text-center text-sm text-navy font-regular font-poppins">
                                        In order to open an investor account,
                                        we&apos;re required by law to collect a
                                        few important details about you. This
                                        infomation helps us comply with SEC
                                        regulations and ensures your account
                                        meets legal standards for crowdfuding
                                        investments. Investor onboarding is
                                        processed through North Capital, our
                                        SEC-registered broker-dealer and trusted
                                        partner in compliance and transaction
                                        handling.
                                    </p>
                                </div>
                            )}

                            {/* Form Start */}

                            <div className="mt-6">
                                <div className="flex justify-center mb-8">
                                    <div className="flex p-1 bg-gray-100 rounded-full shadow-sm">
                                        <button
                                            onClick={() =>
                                                setAuthMethod("credentials")
                                            }
                                            className={`px-5 py-2 text-sm font-semibold rounded-full transition-all duration-200 ${
                                                authMethod === "credentials"
                                                    ? "bg-white text-navy shadow"
                                                    : "text-gray-500"
                                            }`}
                                        >
                                            Email & Password
                                        </button>
                                        {(googleEnabled ||
                                            microsoftEnabled) && (
                                            <button
                                                onClick={() =>
                                                    setAuthMethod("oauth")
                                                }
                                                className={`px-5 py-2 text-sm font-semibold rounded-full transition-all duration-200 ${
                                                    authMethod === "oauth"
                                                        ? "bg-white text-navy shadow"
                                                        : "text-gray-500"
                                                }`}
                                            >
                                                Google / Microsoft
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="flex flex-col justify-center gap-8 md:flex-row">
                                    {authMethod === "credentials" && (
                                        <div className="w-full space-y-4 md:w-1/2">
                                            <div className="flex flex-col">
                                                <label className="block text-sm font-semibold text-navy font-poppins mb-1">
                                                    Email*
                                                </label>
                                                <input
                                                    type="email"
                                                    placeholder="Email"
                                                    value={email}
                                                    onChange={(e) =>
                                                        setEmail(e.target.value)
                                                    }
                                                    className="p-2 border border-navy rounded-xl font-poppins"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-navy font-poppins mb-1">
                                                    Password*
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type={
                                                            showPassword
                                                                ? "text"
                                                                : "password"
                                                        }
                                                        placeholder="Enter your password"
                                                        value={password}
                                                        onChange={(e) =>
                                                            setPassword(
                                                                e.target.value,
                                                            )
                                                        }
                                                        className="w-full p-2 border border-navy rounded-xl font-poppins pr-10"
                                                        required
                                                    />
                                                    <button
                                                        type="button"
                                                        aria-label={
                                                            showPassword
                                                                ? "Hide password"
                                                                : "Show password"
                                                        }
                                                        onClick={() =>
                                                            setShowPassword(
                                                                (prev) => !prev,
                                                            )
                                                        }
                                                        className="absolute flex items-center justify-center w-6 h-6 p-0 m-0 text-gray-500 -translate-y-1/2 bg-transparent border-none cursor-pointer right-3 top-1/2 focus:outline-none"
                                                        tabIndex={0}
                                                    >
                                                        {showPassword ? (
                                                            <FaEyeSlash />
                                                        ) : (
                                                            <FaEye />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-navy font-poppins mb-1">
                                                    Confirm Password*
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type={
                                                            showConfirmPassword
                                                                ? "text"
                                                                : "password"
                                                        }
                                                        placeholder="Confirm your password"
                                                        value={confirmPassword}
                                                        onChange={(e) =>
                                                            setConfirmPassword(
                                                                e.target.value,
                                                            )
                                                        }
                                                        className="w-full p-2 border border-navy rounded-xl font-poppins pr-10"
                                                        required
                                                    />
                                                    <button
                                                        type="button"
                                                        aria-label={
                                                            showConfirmPassword
                                                                ? "Hide password"
                                                                : "Show password"
                                                        }
                                                        onClick={() =>
                                                            setShowConfirmPassword(
                                                                (prev) => !prev,
                                                            )
                                                        }
                                                        className="absolute flex items-center justify-center w-6 h-6 p-0 m-0 text-gray-500 -translate-y-1/2 bg-transparent border-none cursor-pointer right-3 top-1/2 focus:outline-none"
                                                        tabIndex={0}
                                                    >
                                                        {showConfirmPassword ? (
                                                            <FaEyeSlash />
                                                        ) : (
                                                            <FaEye />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="flex justify-end">
                                                <button
                                                    onClick={handleSubmit}
                                                    className="aqi-primary-action w-full rounded-xl bg-navy py-3 font-bold text-white shadow-md hover:bg-navy-2 sm:w-1/2 font-poppins"
                                                >
                                                    SUBMIT
                                                </button>
                                            </div>
                                            <p className="mt-4 text-xs text-center text-gray-500 font-poppins">
                                                By creating this account, you
                                                agree to our{" "}
                                                <a
                                                    href="/docs/terms_of_use_10_2026.pdf"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-navy underline hover:text-navy-2"
                                                >
                                                    Terms & Conditions
                                                </a>
                                            </p>
                                        </div>
                                    )}

                                    {authMethod === "oauth" &&
                                        (pendingOAuthProfile || googleEnabled || microsoftEnabled) && (
                                            <div className="flex flex-col items-center w-full p-6 bg-white border border-gray-100 shadow md:w-96 rounded-xl">
                                                <p className="text-center font-semibold text-navy font-poppins mb-4">
                                                    Continue with your preferred
                                                    account
                                                </p>
                                                <div className="flex flex-col w-full gap-4">
                                                    {pendingOAuthProfile && (
                                                        <>
                                                            <div className="space-y-3 rounded-lg bg-blue-50 p-4">
                                                                <p className="text-sm font-semibold text-navy">
                                                                    {pendingOAuthProfile.provider === "google" ? "Google" : "Microsoft"} account connected
                                                                </p>
                                                                <input
                                                                    type="text"
                                                                    value={pendingOAuthProfile.firstName || ""}
                                                                    aria-label="First name"
                                                                    readOnly
                                                                    className="w-full rounded-lg border border-blue-200 bg-white p-2 text-sm"
                                                                />
                                                                <input
                                                                    type="text"
                                                                    value={pendingOAuthProfile.lastName || ""}
                                                                    aria-label="Last name"
                                                                    readOnly
                                                                    className="w-full rounded-lg border border-blue-200 bg-white p-2 text-sm"
                                                                />
                                                                <input
                                                                    type="email"
                                                                    value={email}
                                                                    aria-label="Email"
                                                                    readOnly
                                                                    className="w-full rounded-lg border border-blue-200 bg-white p-2 text-sm"
                                                                />
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={completePendingOAuthRegistration}
                                                                disabled={isCompletingOAuth}
                                                                className="rounded-[6px] bg-navy p-3 font-semibold text-white disabled:opacity-60"
                                                            >
                                                                {isCompletingOAuth ? "CREATING ACCOUNT..." : "CONTINUE AS INVESTOR"}
                                                            </button>
                                                        </>
                                                    )}
                                                    {!pendingOAuthProfile && googleEnabled && (
                                                        <button
                                                            onClick={handleOAuthSignup(
                                                                "google",
                                                            )}
                                                            className="flex items-center justify-center gap-3 rounded-[6px] border border-navy p-2 transition hover:bg-gray-50 font-poppins"
                                                        >
                                                            <img
                                                                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                                                                alt="Google Logo"
                                                                className="w-5 h-5"
                                                            />
                                                            <span className="text-sm font-medium text-navy font-poppins">
                                                                Continue with
                                                                Google
                                                            </span>
                                                        </button>
                                                    )}
                                                    {!pendingOAuthProfile && microsoftEnabled && (
                                                        <button
                                                            onClick={handleOAuthSignup(
                                                                "microsoft",
                                                            )}
                                                            className="flex items-center justify-center gap-3 rounded-[6px] border border-navy p-2 transition hover:bg-gray-50 font-poppins"
                                                        >
                                                            <img
                                                                src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                                                                alt="Microsoft Logo"
                                                                className="w-5 h-5 ml-4"
                                                            />
                                                            <span className="text-sm font-medium text-navy font-poppins">
                                                                Continue with
                                                                Microsoft
                                                            </span>
                                                        </button>
                                                    )}
                                                </div>
                                                <p className="mt-6 text-xs text-center text-gray-500 font-poppins">
                                                    We’ll use your verified
                                                    email from these services.
                                                </p>
                                            </div>
                                        )}
                                </div>
                            </div>
                        </div>
                    </main>

                    <Footer />
                </div>
            </div>
        </>
    );
}

export default InvestorApplicationForm;
